/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import { CustomElement } from '@adobe/react-webcomponent';

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function createCustomPickerElement(ReactComponent, Model, renderRoot = 'element') {
    class PickerFieldElement extends CustomElement {
        constructor() {
            super();

            // Keep copy of current picker selection
            this._selection = new Set();

            // Initialize picker selection from value attribute
            const multiple = this.hasAttribute('multiple');
            const preSelection = this.getAttribute('value');
            if (multiple && preSelection) {
                preSelection.split(',').forEach(value => this._selection.add(value));
            } else if (preSelection) {
                this._selection.add(preSelection);
            }

            // Register event listeners to listen on React state changes
            this.addEventListener('cif:update-value', this._eventHandler.bind(this));
            this.addEventListener('cif:add-value', this._eventHandler.bind(this));
            this.addEventListener('cif:remove-value', this._eventHandler.bind(this));

            // Internal API object which is a promise that gets resolved once the React component calls the resolve method.
            this._apiResolve = null;
            this._api = new Promise(resolve => (this._apiResolve = resolve));
        }

        _eventHandler(event) {
            const {
                type,
                detail: { value }
            } = event;

            if (type === 'cif:update-value' && value && value.length > 0) {
                this._selection.clear();
                this._selection.add(value);
            } else if (type === 'cif:add-value' && value && value.length > 0) {
                this._selection.add(value);
            } else if (type === 'cif:remove-value' && value && value.length > 0) {
                this._selection.delete(value);
            }
        }

        clear() {
            this._api.then(({ clear }) => clear());
            this._selection.clear();
        }

        getValue() {
            const next = this._selection.values().next();
            if (next && next.value) {
                return next.value;
            }
            return '';
        }

        getValues() {
            return Array.from(this._selection);
        }

        setValue(value) {
            this._api.then(({ setValue }) => setValue(value));
            this._selection.clear();
            if (value && value.length > 0) {
                this._selection.add(value);
            }
        }

        setValues(values) {
            this._api.then(({ setValues }) => setValues(values));
            this._selection.clear();
            if (values.length > 0) {
                values.filter(value => value && value.length > 0).forEach(value => this._selection.add(value));
            }
        }
    }

    PickerFieldElement.domModel = Model;
    PickerFieldElement.renderRoot = renderRoot;
    PickerFieldElement.ReactComponent = ReactComponent;
    if (Model) {
        PickerFieldElement.observedAttributes = Model.prototype.attributes;
    }

    return PickerFieldElement;
}
